import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import companyLogo from '../images/logo.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const quickLinks = [
    { label: 'Home', href: '#home' },
    { label: 'About Us', href: '#about' },
    { label: 'Products', href: '#products' },
    { label: 'Industries', href: '#industries' },
    { label: 'Contact Us', href: '#contact' },
  ];

  const products = [
    'Bearings',
    'Oil Seals',
    'Belts',
    'Auto Parts',
    'Tools',
    'Industrial Equipment',
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: isDark ? '#1a1a2e' : '#f0f0f0',
        color: isDark ? 'white' : 'text.primary',
        pt: { xs: 6, md: 8 },
        pb: 3,
        borderTop: isDark ? 'none' : '1px solid rgba(0,0,0,0.1)',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                component="img"
                src={companyLogo}
                alt="Al Amoudi Bearing"
                sx={{
                  height: 60,
                  width: 'auto',
                  borderRadius: 1,
                  mixBlendMode: isDark ? 'normal' : 'multiply',
                }}
              />
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: '0.9rem', lineHeight: 1.2 }}>
                  MOHAMMED SAEED AL AMOUDI
                  <br />
                  TRADING ESTABLISHMENT
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Industrial Solutions since 2012
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{ opacity: 0.8, mb: 3, lineHeight: 1.8 }}
            >
              Your trusted supplier for Auto Parts, Tools, Oil Seals, Belts, 
              Spare Parts & Industrial Equipment in Saudi Arabia. Quality products, 
              competitive prices, and excellent service.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[
                { icon: <FacebookIcon />, href: '#' },
                { icon: <TwitterIcon />, href: '#' },
                { icon: <LinkedInIcon />, href: '#' },
                { icon: <InstagramIcon />, href: '#' },
                { icon: <WhatsAppIcon />, href: 'https://wa.me/966138342170' },
              ].map((social, index) => (
                <IconButton
                  key={index}
                  href={social.href}
                  target="_blank"
                  sx={{
                    color: isDark ? 'white' : 'text.primary',
                    bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    '&:hover': {
                      bgcolor: '#e57373',
                      color: 'white',
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {quickLinks.map((link, index) => (
                <Link
                  key={index}
                  component="button"
                  onClick={() => scrollToSection(link.href)}
                  sx={{
                    color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                    textDecoration: 'none',
                    textAlign: 'left',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#e57373',
                      paddingLeft: 1,
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Products */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Products
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {products.map((product, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                  }}
                >
                  {product}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={4} md={4}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Contact Info
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <PhoneIcon sx={{ color: '#e57373' }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
                    Telephone
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    +966 13 834 2170
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <SmartphoneIcon sx={{ color: '#e57373' }} />
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
                    Mobile
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    +966 55 540 5414
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    +966 58 343 0459
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <EmailIcon sx={{ color: '#e57373' }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  info@alamoudiest.com.sa
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <LocationOnIcon sx={{ color: '#e57373' }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Dammam, Saudi Arabia
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Riyadh, Saudi Arabia
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />

        {/* Copyright */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            © {currentYear} Al Amoudi Bearing. All Rights Reserved.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            www.alamoudiest.com.sa
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
